import React, { useState } from 'react';
import {
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  Logout,
  Settings,
  Person,
  Business,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from './AuthProvider';
import { useNavigate } from 'react-router-dom';

// Styled components
const UserProfileContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  background: 'linear-gradient(135deg, #1e40af 0%, #0891b2 100%)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
  },
}));

const UserMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: theme.spacing(1),
    minWidth: 280,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
  },
}));

const UserInfoSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: '1px solid #e2e8f0',
}));

const RoleChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: 24,
  backgroundColor: '#e0f2fe',
  color: '#0891b2',
  fontWeight: 500,
}));

interface UserProfileProps {
  showName?: boolean;
}

const UserProfile: React.FC<UserProfileProps> = ({ showName = false }) => {
  const { user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  const handleSettings = () => {
    handleClose();
    navigate('/settings');
  };

  if (!user) {
    return null;
  }

  // Get user initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <UserProfileContainer>
      {showName && (
        <Box sx={{ textAlign: 'right', marginRight: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              fontSize: '0.9rem',
            }}
          >
            {user.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
              fontSize: '0.8rem',
            }}
          >
            {user.jobTitle || 'Employee'}
          </Typography>
        </Box>
      )}

      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ padding: 0 }}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <UserAvatar
          src={user.avatar}
          alt={user.name}
        >
          {!user.avatar && getInitials(user.name)}
        </UserAvatar>
      </IconButton>

      <UserMenu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <UserInfoSection>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <UserAvatar
              src={user.avatar}
              alt={user.name}
              sx={{ width: 48, height: 48 }}
            >
              {!user.avatar && getInitials(user.name)}
            </UserAvatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: '#1e293b',
                  marginBottom: 0.5,
                }}
              >
                {user.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '0.85rem',
                  marginBottom: 1,
                }}
              >
                {user.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {user.roles.map((role) => (
                  <RoleChip
                    key={role}
                    label={role}
                    size="small"
                  />
                ))}
                {user.roles.length === 0 && (
                  <RoleChip
                    label="Employee"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </Box>
          
          {user.department && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 2 }}>
              <Business sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography
                variant="body2"
                sx={{ color: '#64748b', fontSize: '0.85rem' }}
              >
                {user.department}
              </Typography>
            </Box>
          )}
        </UserInfoSection>

        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Account Settings
        </MenuItem>

        <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          View Profile
        </MenuItem>

        <Divider />

        <MenuItem 
          onClick={handleLogout}
          disabled={isLoading}
          sx={{
            color: '#ef4444',
            '&:hover': {
              backgroundColor: '#fef2f2',
            },
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: '#ef4444' }} />
          </ListItemIcon>
          {isLoading ? 'Signing out...' : 'Sign Out'}
        </MenuItem>
      </UserMenu>
    </UserProfileContainer>
  );
};

export default UserProfile;
